import {Action, Actions, DataStatus} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getResponsiveWidgetActions} from './utils/getResponsiveWidgetActions';

interface WidgetHeaderProps extends TestIdProps {
  logo: ReactNode;
  title: string;
  subtitle?: ReactNode;
  actions?: Action[];
  areActionsLoading?: boolean;
  headerActions?: ReactNode;
}

export function WidgetHeader(props: WidgetHeaderProps) {
  const mobileActions = getResponsiveWidgetActions(props.actions ?? []);

  return (
    <Box data-testid={suffixTestId('widgetHeader', props)}>
      <Show onDesktop onNotebook>
        <HStack spacing={4}>
          <WidgetLogo logo={props.logo} variant="default" />

          <VStack spacing={2} grow={1}>
            <HStack justify="space-between" align="center">
              <Heading size={4}>{props.title}</Heading>
              <Show when={isNotNil(props.headerActions)}>{props.headerActions}</Show>
            </HStack>
            <Show when={isNotNil(props.subtitle)}>{props.subtitle}</Show>

            <DataStatus
              isLoading={props.areActionsLoading}
              spinnerSize="small"
              data-testid={suffixTestId('actions', props)}
            >
              <Actions actions={props.actions} data-testid={suffixTestId('actions', props)} />
            </DataStatus>
          </VStack>
        </HStack>
      </Show>

      <Show onMobile onTablet>
        <VStack spacing={2}>
          <HStack spacing={4}>
            <WidgetLogo logo={props.logo} variant="small" />

            <VStack spacing={1}>
              <Heading size={4}>{props.title}</Heading>
              <Show when={isNotNil(props.headerActions)}>{props.headerActions}</Show>
            </VStack>
          </HStack>

          <VStack spacing={2}>
            <Show when={isNotNil(props.subtitle)}>{props.subtitle}</Show>

            <DataStatus
              isLoading={props.areActionsLoading}
              spinnerSize="small"
              data-testid={suffixTestId('actions-mobile', props)}
            >
              {isNotNil(mobileActions) && (
                <HStack spacing={2} align="center">
                  <Box flexGrow={1} flexBasis={0}>
                    <Actions
                      actions={[mobileActions.primaryAction]}
                      data-testid={suffixTestId('actions-mobile', props)}
                    />
                  </Box>
                  {isNotNil(mobileActions.overflowAction) && (
                    <Actions
                      actions={[mobileActions.overflowAction]}
                      data-testid={suffixTestId('actions-mobile-overflow', props)}
                    />
                  )}
                </HStack>
              )}
            </DataStatus>
          </VStack>
        </VStack>
      </Show>
    </Box>
  );
}

const WIDGET_LOGO_SIZE = {
  default: 24,
  small: 16,
} as const;

type WidgetLogoVariant = keyof typeof WIDGET_LOGO_SIZE;

function WidgetLogo(props: {logo: ReactNode; variant: WidgetLogoVariant}) {
  const size = WIDGET_LOGO_SIZE[props.variant];

  return (
    <Box
      borderRadius="small"
      height={size}
      minHeight={size}
      minWidth={size}
      overflow="hidden"
      width={size}
    >
      {props.logo}
    </Box>
  );
}
